import { useCallback } from 'react';
import { UseFormSetError, FieldPath, FieldValues } from 'react-hook-form-v7';
import { useTranslation } from 'react-i18next';

import { isAxiosError, hasApiErrorResponse, extractErrorInfo } from '@personnel/application/errors';
import {
  shouldHandleInForm,
  getFormFieldForError,
  getFormMessageKeyForError,
  FormFieldMapping,
} from '@personnel/domain/errors/formFieldMappings';

interface FormErrorResult {
  /** Whether the error was handled and set on a form field */
  handled: boolean;
  /** The field name where the error was set */
  fieldName?: string;
  /** The error message from the backend */
  message?: string;
}

/**
 * Hook for handling API errors and setting them on form fields
 *
 * This hook attempts to extract field-specific errors from API responses
 * and set them on the appropriate form fields using react-hook-form's setError.
 *
 * @param mappings - Form field mappings to use for error handling
 * @returns Object with handleFormError function
 *
 * @example
 * const { handleFormError } = useFormErrorHandler<EmployeeFormData>(EMPLOYEE_FORM_FIELD_MAPPINGS);
 *
 * const onError = (error: Error) => {
 *   const result = handleFormError(error, setError);
 *   if (result.handled) {
 *     // Error was set on form field
 *     toast.error('Ocurrió un error. Intenta nuevamente.');
 *   } else {
 *     // Handle with generic error handler
 *     handleError(error);
 *   }
 * };
 */
export const useFormErrorHandler = <TFormData extends FieldValues>(mappings: FormFieldMapping[]) => {
  const { t } = useTranslation();

  /**
   * Attempts to handle an API error by setting it on the appropriate form field
   *
   * @param error - The error from the API call
   * @param setError - react-hook-form's setError function
   * @returns Object indicating if error was handled and which field
   */
  const handleFormError = useCallback(
    (error: unknown, setError: UseFormSetError<TFormData>): FormErrorResult => {
      // Only handle Axios errors with structured API response
      if (!isAxiosError(error) || !hasApiErrorResponse(error)) {
        return { handled: false };
      }

      const statusCode = error.response?.status || 500;
      const apiErrorResponse = error.response?.data;

      // Don't handle server errors (5xx) or missing response
      if (!apiErrorResponse || statusCode >= 500) {
        return { handled: false };
      }

      const errorInfo = extractErrorInfo(apiErrorResponse, statusCode);

      // Check if this error should be handled in the form
      if (!shouldHandleInForm(errorInfo.code, mappings)) {
        return { handled: false };
      }

      const fieldName = getFormFieldForError(errorInfo.code, mappings);

      if (!fieldName) {
        return { handled: false };
      }

      // A mapping with its own copy wins: the backend message is not translated.
      const messageKey = getFormMessageKeyForError(errorInfo.code, mappings);
      const message = messageKey ? t(messageKey) : errorInfo.message;

      setError(fieldName as FieldPath<TFormData>, {
        type: 'server',
        message,
      });

      return {
        handled: true,
        fieldName,
        message,
      };
    },
    [mappings, t],
  );

  return { handleFormError };
};
